// ===============================
// src/pages/admin/RecivedOffersAdmin.jsx
// ===============================
import React, { Suspense, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_DOMAIN } from "config";

import { Heading, Img, Button } from "../../components";
import AllOffersProviderLinesRowSeven from "../../components/AllOffersProviderLinesRowSeven";
import ApprovedOffersAdmin from "../../components/ApprovedOffersAdmin";
import ApproveOfferByProviderPopup from "components/ApproveOfferByProviderPopup";
import { hasActiveJ5Hold } from "utils/j5Status";

const tabs = ["הצעות חדשות", "הצעות שאושרו"];
const STATUS_MAP = { "הצעות חדשות": 1, "הצעות שאושרו": 2 };

const sortOffersDesc = (offers) => {
  const arr = Array.isArray(offers) ? [...offers] : [];
  arr.sort((a, b) => {
    const da = new Date(a.offerDate || a.creation_date || 0).getTime();
    const db = new Date(b.offerDate || b.creation_date || 0).getTime();
    return db - da;
  });
  return arr;
};

export default function RecivedOffersAdmin({ currentUser }) {
  const [activeTab, setActiveTab] = useState("הצעות חדשות");

  const [showApprovedOfferPopuop, setShowApprovedOfferPopuop] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [offerGroups, setOfferGroups] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, totalItems: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => setPage(1), [activeTab]);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
      const status = STATUS_MAP[activeTab];

      const { data } = await axios.get(`${API_DOMAIN}/offer/getAdminReceivedOffers`, {
        params: { status, page, pageSize },
      });

      setMeta(data?.meta || { page: 1, pageSize, totalItems: 0, totalPages: 1 });

      const mapped = (data?.data || []).map((b) => ({
        car: {
          ...b.car,
          images: (b.images || []).map((u) => ({ image_url: u })),
        },
        offers: (b.offers || []).map((o) => ({
          id: o.id,

          buyerName: o.buyer_name || "",
          buyerContactName: o.buyer_contact_name,
          buyerPhoneNumber: o.buyer_phone_number,

          providerName: b?.car?.provider_name,
          providerBusinessName: b?.car?.provider_business_name,

          carPrice: b?.car?.asking_price,
          offerPrice: o.offered_price,
          price: o.offered_price,

          offerDate: o.creation_date,
          offerPosition: o.offer_position,
          collectionDate: o.collection_date,
          lastPaymentDate: o.last_payment_date,
          reservation_hold_id: o.reservation_hold_id,
          reservation_hold_status: o.reservation_hold_status,
          hold_status: o.hold_status,
          isReserved: hasActiveJ5Hold(o),

          status: o.status,
          statusLabel: o.status === 1 ? "ממתין לאישור" : o.status === 2 ? "מאושר" : "נדחה",

          star: o.star,
          rating: o.rating,
          rate: o.rate,
        })),
      }));

      setOfferGroups(mapped);
    } catch (e) {
      console.error(e);
      setOfferGroups([]);
      setMeta((m) => ({ ...m, totalItems: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, pageSize]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await loadOffers();
    };

    run();
    const intervalId = setInterval(run, 120000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [currentUser?.id, loadOffers]);

  const handlePopupClose = () => setShowApprovedOfferPopuop(false);
  const UpdateShowApprovedOfferPopup = (state) => setShowApprovedOfferPopuop(!!state);

  const handleOfferApproved = (carId) => {
    setOfferGroups((prev) => prev.filter((g) => g.car.id !== carId));
  };

  const statusForNav = STATUS_MAP[activeTab];

  // ✅ הכפתור "כל ההצעות" עובר לתוך הקומפוננטות (שורה של רכב/שנה) דרך prop
  const preparedGroups = useMemo(() => {
    return (offerGroups || []).map((g) => {
      const all = sortOffersDesc(g.offers);

      const leftAction = (
        <div className="shrink-0 flex justify-start">
          <Button
            size="lg"
            shape="round"
            className="rounded-[16px] border border-solid border-gray-300 px-4 py-2 font-semibold bg-white"
            onClick={() => {
              navigate(`/admin/received-offers/${g.car.id}?status=${statusForNav}`, {
                state: { car: g.car, offers: g.offers, activeTab },
              });
            }}
          >
            כל ההצעות ({all.length})
          </Button>
        </div>
      );

      return {
        ...g,
        previewOffers: all.slice(0, 3),
        allOffersCount: all.length,
        leftAction,
      };
    });
  }, [offerGroups, navigate, statusForNav, activeTab]);

  return (
    <>
      <div className="flex bg-blue_gray-50 p-8 sm:p-4" style={{ width: "94%", paddingTop: "1rem" }}>
        <div className="w-[100%] items-start gap-4 md:flex-col" style={{ gap: "2%" }}>
          <div className="flex relative flex-1 flex-col gap-6 self-center rounded-[24px] bg-secondary-primary_white p-6 md:self-stretch sm:p-4">
            {/* HEADER */}
            <header className="flex flex-col gap-5">
              <div dir="rtl" className="flex items-center justify-between gap-4 md:flex-col md:items-stretch">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-2xl"
                    style={{ backgroundColor: "#ce000014" }}
                  >
                    <Img src="/images/img_user_red_a700_03_1.svg" alt="" className="h-[22px] w-[22px]" />
                  </div>
                  <div className="flex flex-col">
                    <Heading size="heading9xl" as="h4" className="!font-heebo text-[24px] font-bold lg:text-[20px]">
                      הצעות מחיר שהתקבלו
                    </Heading>
                    <span className="text-[13px] text-gray-500">
                      סה״כ {meta.totalItems} רכבים עם הצעות פעילות
                    </span>
                  </div>
                </div>

                {/* Segmented tab control */}
                <div className="flex rounded-2xl bg-gray-50 p-1 md:w-full">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`rounded-xl px-5 py-2.5 text-[14px] font-semibold transition-all duration-200 md:flex-1 ${
                        activeTab === tab
                          ? "bg-white text-red-a700_03"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-px w-full bg-gray-200" />
            </header>

            <div className="flex flex-col gap-5">
              <Suspense fallback={<div className="py-10 text-center text-gray-400">…טוען…</div>}>
                {loading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-400">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-red-a700_03" />
                    <span className="text-sm">טוען הצעות…</span>
                  </div>
                ) : preparedGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 py-16 text-gray-400">
                    <Img src="/images/img_user_red_a700_03_1.svg" alt="" className="h-[32px] w-[32px] opacity-30" />
                    <span className="text-sm font-medium">אין הצעות להצגה בטאב זה</span>
                  </div>
                ) : (
                  preparedGroups.map((g) => (
                    <div
                      key={g.car.id}
                      className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-3"
                    >
                      {activeTab === "הצעות שאושרו" ? (
                        <ApprovedOffersAdmin
                          car={g.car}
                          rows={g.previewOffers}
                          onOfferApproved={handleOfferApproved}
                          updateShowApprovedOfferPopup={UpdateShowApprovedOfferPopup}
                          setSelectedCar={setSelectedCar}
                          setSelectedOffer={setSelectedOffer}
                        />
                      ) : (
                        <AllOffersProviderLinesRowSeven
                          car={g.car}
                          rows={g.previewOffers}
                          onOfferApproved={handleOfferApproved}
                          updateShowApprovedOfferPopup={UpdateShowApprovedOfferPopup}
                          setSelectedCar={setSelectedCar}
                          setSelectedOffer={setSelectedOffer}
                        />
                      )}
                      <div className="flex justify-end mt-3">{g.leftAction}</div>
                    </div>
                  ))
                )}
              </Suspense>

              {/* PAGINATION */}
              <div dir="rtl" className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3 md:flex-col md:items-stretch md:gap-3">
                <div className="text-sm font-medium text-gray-600">
                  עמוד <span className="text-gray-900">{meta.page}</span> מתוך{" "}
                  <span className="text-gray-900">{meta.totalPages}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  סה״כ רכבים: <span className="text-gray-900">{meta.totalItems}</span>
                </div>

                <div className="flex items-center gap-2 md:justify-between">
                  <div className="flex items-center overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <button
                      type="button"
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30"
                      disabled={meta.page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <span>הקודם</span>
                    </button>
                    <div className="h-5 w-px bg-gray-200" />
                    <button
                      type="button"
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30"
                      disabled={meta.page >= meta.totalPages}
                      onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                    >
                      <span>הבא</span>
                    </button>
                  </div>

                  <div className="relative">
                    <select
                      dir="rtl"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="cursor-pointer rounded-xl border border-gray-200 bg-white py-1.5 pl-8 pr-3 text-sm font-medium text-gray-700 appearance-none"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        appearance: "none",
                        backgroundImage: "none",
                      }}
                    >
                      {[10, 5, 3, 2, 1].map((n) => (
                        <option key={n} value={n}>
                          {n} רכבים בעמוד
                        </option>
                      ))}
                    </select>

                    <Img
                      src="/images/img_vector_red_a700_03_10.svg"
                      alt=""
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-[9px] w-[9px] opacity-60"
                    />
                  </div>
                </div>
              </div>
            </div>

            {showApprovedOfferPopuop && (
              <ApproveOfferByProviderPopup onClose={handlePopupClose} car={selectedCar} offer={selectedOffer} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
